import { useEffect, useRef, useState } from 'react';

/** Minimum time the workspace loader stays visible once shown (auth + prep combined). */
export const WORKSPACE_LOADER_MIN_MS = 2500;

function getLoadSessionKey() {
  if (typeof window === 'undefined') return 'workspace_loader_default';
  return `workspace_loader_${window.performance.timeOrigin}`;
}

function readSessionStart() {
  try {
    const raw = sessionStorage.getItem(getLoadSessionKey());
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function writeSessionStart(ts) {
  try {
    sessionStorage.setItem(getLoadSessionKey(), String(ts));
  } catch {
    // ignore
  }
}

function clearSessionStart() {
  try {
    sessionStorage.removeItem(getLoadSessionKey());
  } catch {
    // ignore
  }
}

export function markWorkspaceLoaderStarted() {
  if (readSessionStart() === null) {
    writeSessionStart(Date.now());
  }
}

/**
 * Keeps the loader visible for at least minMs from the first active moment in this
 * page load. Uses a per-reload sessionStorage key so remounts do not reset the timer.
 */
export function useWorkspaceLoaderVisibility(isActive, minMs = WORKSPACE_LOADER_MIN_MS) {
  const [visible, setVisible] = useState(() => {
    if (isActive) return true;
    const started = readSessionStart();
    if (started === null) return false;
    return Date.now() - started < minMs;
  });
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    if (isActive) {
      clearHideTimer();
      if (readSessionStart() === null) {
        writeSessionStart(Date.now());
      }
      setVisible(true);
      return clearHideTimer;
    }

    const started = readSessionStart();
    if (started === null) {
      setVisible(false);
      return undefined;
    }

    const remaining = minMs - (Date.now() - started);
    if (remaining <= 0) {
      clearSessionStart();
      setVisible(false);
      return undefined;
    }

    hideTimerRef.current = window.setTimeout(() => {
      clearSessionStart();
      setVisible(false);
      hideTimerRef.current = null;
    }, remaining);

    return clearHideTimer;
  }, [isActive, minMs]);

  return visible;
}

/** @deprecated Use useWorkspaceLoaderVisibility */
export const useMinimumVisibleLoading = useWorkspaceLoaderVisibility;
